/**
 * My Devices — active sessions with server-side revocation (P4).
 *
 * Mounted under ME → Security → Devices. Never exposes IPs or precise
 * geolocation; only coarse city + browser/OS family.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Monitor, Smartphone, ChevronLeft, LogOut, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

function DeviceIcon({ name }) {
  const isMobile = /iPhone|Android|iPad/i.test(name || "");
  const Icon = isMobile ? Smartphone : Monitor;
  return <Icon size={18}/>;
}

export default function MyDevices() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(true);
  const [revoking, setRevoking] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/me/sessions");
      setSessions(data.sessions || []);
    } catch { toast.error(t("devices.load_error", "Could not load devices")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (jti) => {
    try {
      await api.delete(`/me/sessions/${jti}`);
      setSessions(sessions.filter(s => s.jti !== jti));
      toast.success(t("devices.revoked", "Session revoked"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error", "Something went wrong"));
    }
  };
  const logoutOthers = async () => {
    setRevoking(true);
    try {
      const { data } = await api.post("/me/sessions/logout-others");
      toast.success(t("devices.logout_others_ok", "Logged out {n} other device(s)", { n: data.revoked }));
      load();
    } catch { toast.error(t("common.error", "Something went wrong")); }
    finally { setRevoking(false); }
  };

  const others = sessions.filter(s => !s.current).length;

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="my-devices-page">
      <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => nav(-1)} aria-label={t("common.back", "Back")} data-testid="devices-back">
            <ChevronLeft size={20}/>
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-black">{t("me.security", "Security")}</div>
            <div className="font-black text-stone-900 text-base flex items-center gap-2">
              <ShieldCheck size={18}/> {t("devices.title", "My devices")}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-3">
        <div className="rounded-2xl bg-white border border-stone-200 p-4 text-xs text-stone-600">
          {t("devices.intro", "These are the devices currently signed in to your account. If you don't recognize a device, log it out immediately.")}
        </div>

        {busy && <div className="text-center py-10"><Loader2 className="animate-spin inline text-stone-400"/></div>}

        {!busy && sessions.map(s => (
          <div key={s.jti} data-testid={`device-row-${s.jti}`}
               className="rounded-2xl bg-white border border-stone-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-stone-100 text-stone-700 grid place-items-center">
              <DeviceIcon name={s.device_name}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-stone-900 text-sm flex items-center gap-2">
                {s.device_name || t("devices.unknown", "Unknown device")}
                {s.current && (
                  <span className="text-[10px] px-1.5 rounded-full bg-emerald-100 text-emerald-700 font-black" data-testid={`device-current-${s.jti}`}>
                    {t("devices.this_device", "This device")}
                  </span>
                )}
                {s.two_factor && (
                  <span className="text-[10px] px-1.5 rounded-full bg-sky-100 text-sky-700 font-black" title={t("devices.mfa", "MFA verified")}>
                    2FA
                  </span>
                )}
              </div>
              <div className="text-[11px] text-stone-500 mt-0.5">
                {s.city ? `${s.city} · ` : ""}
                {t("devices.last_active", "Last active")}: {s.last_active?.slice(0, 16).replace("T", " ") || "—"}
              </div>
            </div>
            {!s.current && (
              <button onClick={() => revoke(s.jti)}
                      data-testid={`device-revoke-${s.jti}`}
                      className="text-xs font-black rounded-full bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 px-3 py-1.5 inline-flex items-center gap-1">
                <LogOut size={12}/> {t("devices.logout", "Log out")}
              </button>
            )}
          </div>
        ))}

        {!busy && others > 0 && (
          <button onClick={logoutOthers} disabled={revoking}
                  data-testid="devices-logout-others"
                  className="w-full py-3 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-black text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <LogOut size={14}/> {revoking ? t("common.working", "Working…") : t("devices.logout_others", "Log out all {n} other device(s)", { n: others })}
          </button>
        )}
      </div>
    </div>
  );
}
